/**
 * IvoryToursPresentation — private B2B presentation for Ivory Tours.
 *
 * Route: /presentations/ivory-tours   (not in main navigation / sitemap)
 * Isolation: standalone component with its own palette and layout.
 * Content source of truth: /app/frontend/public/downloads/German_Tourism_Support_Sao_Tome.pdf
 *
 * Also provides an optional PRESENTATION MODE triggered with ?present=1 or by
 * pressing "P". Keyboard arrows then navigate between sections. The normal
 * scrolling page remains fully functional when presentation mode is off.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, MessageCircle, Compass, Bird, Clock, Hourglass, Users,
  Building2, Leaf, ArrowRight, ChevronLeft, ChevronRight, Play, X,
  GraduationCap, Handshake, BarChart3, Download, ExternalLink, Plus,
} from "lucide-react";

/* ============================= PALETTE ================================= */
const T = {
  bg:      "#0d1f17",   // deep forest
  bg2:     "#122a1f",   // medium forest
  bg3:     "#193726",   // elevated surface
  border:  "rgba(245, 239, 224, 0.08)",
  borderS: "rgba(245, 239, 224, 0.18)",
  ivory:   "#f5efe0",
  ivoryD:  "#d6cfbd",
  mute:    "#a9a493",
  gold:    "#c9a24a",
  goldD:   "#8b6f31",
  leaf:    "#4ea878",
  deep:    "#0a1711",
};

/* ============================= DATA (verbatim from PDF) =============== */
const SECTIONS = [
  { id: "hero",      label: "Opening" },
  { id: "need",      label: "The Need" },
  { id: "capacity",  label: "Local Capacity" },
  { id: "support",   label: "DE Support" },
  { id: "operating", label: "Operating Model" },
  { id: "usecase",   label: "Use Case" },
  { id: "levels",    label: "Support Levels" },
  { id: "pilot",     label: "Pilot Proposal" },
  { id: "questions", label: "Key Questions" },
  { id: "nextsteps", label: "Next Steps" },
  { id: "cta",       label: "Discussion" },
];

const CAPACITY_CARDS = [
  { k: "Eco Collection",
    desc: "Coordination, structuring experiences and linking between operator, client and local partners.",
    icon: Leaf,
    accent: T.leaf },
  { k: "Local Guides",
    desc: "Identified local guides for experiences, with support in English and French.",
    icon: Compass,
    accent: T.gold },
  { k: "Specialized Birdwatching",
    desc: "Endemic specialists. Deep knowledge of the territory and birds of São Tomé, including identification of species by their English names.",
    icon: Bird,
    accent: T.ivory },
];

const OPERATING_FLOW = [
  { k: "Ivory Tours",            s: "Client / German-speaking group",      icon: Building2 },
  { k: "Eco Collection",         s: "Local coordination and point of contact", icon: Leaf },
  { k: "Local Operator / Guide", s: "Execution of the experience",         icon: Compass },
  { k: "German-Speaking Support",s: "Linguistic accompaniment",            icon: MessageCircle },
  { k: "Guest Experience",       s: "The outcome",                         icon: Users },
];

const USECASE_META = [
  { icon: Users,     k: "Local specialist", v: "Mito" },
  { icon: MapPin,    k: "Area",             v: "Obô Natural Park / Monte Carmo" },
  { icon: Clock,     k: "Typical start",    v: "05:30 – 06:00" },
  { icon: Hourglass, k: "Duration",         v: "5 – 6 hours" },
  { icon: Users,     k: "Format",           v: "Private / small groups" },
];

const SUPPORT_LEVELS = [
  { k: "Local Experience",                  s: "Guide / local operation" },
  { k: "Local Experience + EN / FR Support", s: "For international clients" },
  { k: "Local Experience + DE Support",     s: "Local guide + German-speaking accompaniment" },
  { k: "Special Interest",                  s: "Birdwatching, nature trekking and other specialized experiences" },
  { k: "Tailor-Made Support",               s: "Specific programs for Ivory Tours groups" },
];

const PILOT_RECIPE = [
  { icon: Building2,       k: "1 operator",             s: "Ivory Tours" },
  { icon: MapPin,          k: "1 – 2 experiences",      s: "" },
  { icon: Compass,         k: "1 local guide",          s: "" },
  { icon: MessageCircle,   k: "1 German-speaking support", s: "" },
  { icon: Users,           k: "1 small group",          s: "First clients" },
];

const QUESTIONS = [
  "In which moments do clients need German support? (Airport arrival? Hotel assistance? Excursions? Full-day or entire itinerary?)",
  "What is the expected German level? (Is B2 sufficient?)",
  "Should the support be purely linguistic, or also include operational accompaniment?",
  "Which types of experiences have the highest demand from your clients?",
  "Are your bookings primarily individual travellers or groups?",
  "With how much advance notice are client dates usually known?",
  "Would Ivory Tours be willing to test this as a first pilot operation?",
];

const NEXT_STEPS = [
  { icon: MapPin,        k: "Ivory Tours",     s: "Defines a real need / first case" },
  { icon: Leaf,          k: "Eco Collection",  s: "Structures the operation" },
  { icon: GraduationCap, k: "CLA / USTP",      s: "Identification of a suitable German-speaking profile (B2)" },
  { icon: Handshake,     k: "Local Partner",   s: "Execution" },
  { icon: BarChart3,     k: "Pilot Operation", s: "Evaluation" },
];

/* ============================= PRIMITIVES ============================= */
const Container = ({ children, className = "" }) => (
  <div className={`max-w-6xl mx-auto px-6 lg:px-10 ${className}`}>{children}</div>
);

const Eyebrow = ({ children, color = T.gold }) => (
  <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.32em] font-semibold"
       style={{ color }}>
    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
    {children}
  </div>
);

const SectionTitle = ({ eyebrow, title, subtitle, max = "max-w-3xl" }) => (
  <div className={max}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="mt-4 font-semibold tracking-tight text-[28px] sm:text-[34px] lg:text-[42px] leading-[1.08]"
        style={{ color: T.ivory, letterSpacing: "-0.02em" }}>
      {title}
    </h2>
    {subtitle && (
      <p className="mt-4 text-[14px] sm:text-[15px] leading-relaxed" style={{ color: T.ivoryD }}>
        {subtitle}
      </p>
    )}
  </div>
);

const StatusPill = ({ children, color = T.gold }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9.5px] uppercase tracking-[0.28em] font-semibold"
        style={{ background: `${color}1a`, border: `1px solid ${color}55`, color }}>
    <span className="w-1 h-1 rounded-full" style={{ background: color }} />{children}
  </span>
);

/* =========================== SECTIONS ================================= */
function Hero() {
  return (
    <section id="hero" className="relative min-h-[88vh] flex items-center overflow-hidden" style={{ background: T.deep }}>
      {/* Background photo */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1623266260849-a6a12ab1c1cb?auto=format&fit=crop&w=2400&q=70"
          alt=""
          loading="eager"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0" style={{
          background: `linear-gradient(100deg, ${T.deep} 0%, rgba(10,23,17,0.72) 55%, rgba(10,23,17,0.3) 100%)`
        }} />
      </div>
      <Container className="relative z-10 py-24">
        <div className="flex items-center gap-3 flex-wrap text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: T.gold }}>
          <span>Eco Collection</span>
          <span style={{ color: T.ivoryD, opacity: 0.6 }}>×</span>
          <span>Ivory Tours</span>
          <span style={{ color: T.ivoryD, opacity: 0.4 }}>·</span>
          <span style={{ color: T.ivoryD }}>by BSGOTECH</span>
        </div>
        <h1 className="mt-8 font-semibold tracking-tight text-[40px] sm:text-[52px] lg:text-[68px] leading-[1.02]"
            style={{ color: T.ivory, letterSpacing: "-0.025em" }}>
          German-Speaking <br className="hidden sm:block" />
          <span style={{ color: T.gold }}>Tourism Support</span>
        </h1>
        <div className="mt-5 text-[18px] sm:text-[22px]" style={{ color: T.ivory, opacity: 0.95 }}>
          São Tomé &amp; Príncipe
        </div>
        <p className="mt-6 max-w-2xl text-[15px] sm:text-[17px] leading-relaxed" style={{ color: T.ivoryD }}>
          From local expertise to multilingual guest support.
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <StatusPill color={T.gold}>Private meeting · 16 October 2026</StatusPill>
          <StatusPill color={T.leaf}>Pilot proposal</StatusPill>
        </div>
        <div className="mt-10 flex items-center gap-6 text-[11px] uppercase tracking-[0.28em]" style={{ color: T.ivoryD, opacity: 0.75 }}>
          <span>Prepared by BSGOTECH</span>
          <span style={{ opacity: 0.5 }}>·</span>
          <a href="#need" className="hover:underline" style={{ color: T.gold }}>Scroll to begin ↓</a>
        </div>
      </Container>
    </section>
  );
}

function NeedSection() {
  return (
    <section id="need" className="py-24 lg:py-32" style={{ background: T.bg }}>
      <Container>
        <SectionTitle
          eyebrow="A need identified in the destination"
          title="Limited availability of local support in German language."
          subtitle="In our previous conversation, Ivory Tours identified a key operational challenge: German-language support is scarce on the ground in São Tomé & Príncipe. Eco Collection is structuring a local solution to complement existing operators and guides."
        />
        <div className="mt-12 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-stretch">
          <div className="rounded-2xl p-8 lg:p-10" style={{ background: T.bg2, border: `1px solid ${T.border}` }}>
            <Eyebrow color={T.leaf}>What Eco Collection combines</Eyebrow>
            <ul className="mt-6 space-y-5">
              {[
                { k: "Local knowledge",            s: "Operators and guides already active in São Tomé & Príncipe." },
                { k: "German communication",       s: "Linguistic support designed for German-speaking visitors." },
                { k: "Operational coordination",    s: "A single point of contact that ties the operation together." },
              ].map((it, i) => (
                <li key={it.k} className="flex gap-4">
                  <div className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center font-mono text-[12px] font-semibold"
                       style={{ background: `${T.gold}18`, border: `1px solid ${T.gold}44`, color: T.gold }}>
                    0{i + 1}
                  </div>
                  <div>
                    <div className="text-[15px] font-semibold" style={{ color: T.ivory }}>{it.k}</div>
                    <div className="mt-1 text-[13px] leading-relaxed" style={{ color: T.ivoryD }}>{it.s}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl overflow-hidden relative min-h-[320px]" style={{ border: `1px solid ${T.border}` }}>
            <img src="https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=70"
                 alt="Lush São Tomé rainforest path" loading="lazy"
                 className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 55%, ${T.deep} 100%)` }} />
            <div className="absolute bottom-6 left-6 right-6">
              <StatusPill color={T.gold}>São Tomé · Nature · Culture · Community</StatusPill>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function CapacitySection() {
  return (
    <section id="capacity" className="py-24 lg:py-32" style={{ background: T.bg2 }}>
      <Container>
        <SectionTitle
          eyebrow="Local capacity"
          title="Local capacity is being structured."
          subtitle="Eco Collection, local guides and endemic specialists — a layer of coordination that works alongside existing operators."
        />
        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {CAPACITY_CARDS.map((c) => (
            <div key={c.k} className="rounded-2xl p-6 flex flex-col"
                 style={{ background: T.bg3, border: `1px solid ${T.border}`, borderTop: `2px solid ${c.accent}88` }}>
              <div className="w-11 h-11 rounded-md flex items-center justify-center"
                   style={{ background: `${c.accent}18`, border: `1px solid ${c.accent}44` }}>
                <c.icon size={20} style={{ color: c.accent }} />
              </div>
              <div className="mt-5 text-[18px] font-semibold" style={{ color: T.ivory }}>{c.k}</div>
              <p className="mt-3 text-[13.5px] leading-relaxed" style={{ color: T.ivoryD }}>{c.desc}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

function SupportSection() {
  return (
    <section id="support" className="py-24 lg:py-32" style={{ background: T.bg }}>
      <Container>
        <SectionTitle
          eyebrow="German-Speaking Tourism Support"
          title={<>Eco Collection <span style={{ color: T.gold }}>+ CLA / USTP</span></>}
          subtitle="The German Language Centre of USTP has expressed interest in analysing a pilot phase."
        />
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            "~5 people currently at B2 level",
            "A new stream focused on the tourism sector",
            "Practical experience and real-world communication",
          ].map((x) => (
            <div key={x} className="rounded-xl p-5" style={{ background: T.bg2, border: `1px solid ${T.border}` }}>
              <GraduationCap size={16} style={{ color: T.gold }} />
              <div className="mt-3 text-[13.5px] leading-relaxed" style={{ color: T.ivory }}>{x}</div>
            </div>
          ))}
        </div>
        {/* Formula */}
        <div className="mt-14 rounded-2xl p-6 lg:p-8" style={{ background: T.bg2, border: `1px solid ${T.border}` }}>
          <Eyebrow color={T.leaf}>The combination</Eyebrow>
          <div className="mt-6 grid md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-5">
            {[
              { k: "Local Guide",            s: "Destination knowledge",            icon: MapPin },
              { k: "German-speaking support", s: "Communication with the visitor",  icon: MessageCircle },
              { k: "Eco Collection",         s: "Coordination and operational control", icon: Leaf },
            ].map((n, i, arr) => (
              <React.Fragment key={n.k}>
                <div className="rounded-xl p-5" style={{ background: T.bg3, border: `1px solid ${T.border}` }}>
                  <n.icon size={20} style={{ color: T.gold }} />
                  <div className="mt-3 text-[15px] font-semibold" style={{ color: T.ivory }}>{n.k}</div>
                  <div className="mt-1 text-[12.5px]" style={{ color: T.ivoryD }}>{n.s}</div>
                </div>
                {i < arr.length - 1 && (
                  <div className="flex justify-center" aria-hidden="true">
                    <Plus size={22} style={{ color: T.gold, opacity: 0.7 }} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        <p className="mt-6 text-[12px] italic" style={{ color: T.mute }}>
          Pilot structure proposed by Eco Collection — operational scale and partners to be confirmed.
        </p>
      </Container>
    </section>
  );
}

function OperatingSection() {
  return (
    <section id="operating" className="py-24 lg:py-32" style={{ background: T.bg2 }}>
      <Container>
        <SectionTitle
          eyebrow="Operating Model"
          title="A simple and collaborative structure."
          subtitle="Five roles. One point of coordination. One guest experience."
        />
        <div className="mt-14 grid lg:grid-cols-[repeat(9,1fr)] gap-3 items-stretch">
          {OPERATING_FLOW.map((n, i, arr) => (
            <React.Fragment key={n.k}>
              <div className="col-span-2 rounded-2xl p-5 flex flex-col"
                   style={{ background: T.bg3, border: `1px solid ${T.border}`, borderTop: `2px solid ${T.gold}66` }}>
                <div className="w-10 h-10 rounded-md flex items-center justify-center"
                     style={{ background: `${T.gold}18`, border: `1px solid ${T.gold}44` }}>
                  <n.icon size={18} style={{ color: T.gold }} />
                </div>
                <div className="mt-4 text-[14px] font-semibold uppercase tracking-[0.08em]" style={{ color: T.ivory }}>{n.k}</div>
                <div className="mt-1 text-[12.5px] leading-relaxed" style={{ color: T.ivoryD }}>{n.s}</div>
              </div>
              {i < arr.length - 1 && (
                <div className="hidden lg:flex items-center justify-center" aria-hidden="true">
                  <ArrowRight size={18} style={{ color: T.gold, opacity: 0.7 }} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </Container>
    </section>
  );
}

function UseCaseSection() {
  return (
    <section id="usecase" className="py-24 lg:py-32" style={{ background: T.bg }}>
      <Container>
        <SectionTitle
          eyebrow="Real use case"
          title="Endemic Birdwatching · São Tomé"
          subtitle="Explore the forests of São Tomé in search of endemic birds, with a local specialist with deep knowledge of the territory."
        />
        <div className="mt-12 grid lg:grid-cols-[1.1fr_1fr] gap-6">
          {/* Meta */}
          <div className="rounded-2xl p-8" style={{ background: T.bg2, border: `1px solid ${T.border}` }}>
            <ul className="grid sm:grid-cols-2 gap-5">
              {USECASE_META.map((m) => (
                <li key={m.k} className="flex gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center"
                       style={{ background: `${T.leaf}18`, border: `1px solid ${T.leaf}44` }}>
                    <m.icon size={16} style={{ color: T.leaf }} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: T.mute }}>{m.k}</div>
                    <div className="mt-0.5 text-[14px] font-semibold" style={{ color: T.ivory }}>{m.v}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-8 rounded-xl p-5" style={{ background: `${T.gold}0d`, border: `1px solid ${T.gold}44` }}>
              <Eyebrow color={T.gold}>Extended Expedition · 2–3 nights</Eyebrow>
              <p className="mt-3 text-[13px] leading-relaxed" style={{ color: T.ivoryD }}>
                May be considered for specialized birdwatchers, with the possibility of camping and porters, upon prior arrangement and subject to operational confirmation.
              </p>
            </div>
          </div>
          {/* Image */}
          <div className="rounded-2xl overflow-hidden relative min-h-[360px]" style={{ border: `1px solid ${T.border}` }}>
            <img src="https://images.unsplash.com/photo-1555169062-013468b47731?auto=format&fit=crop&w=1600&q=70"
                 alt="Endemic bird on a branch" loading="lazy"
                 className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 60%, ${T.deep} 100%)` }} />
            <div className="absolute bottom-6 left-6 right-6">
              <StatusPill color={T.leaf}>Obô · Monte Carmo · Endemic species</StatusPill>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

function LevelsSection() {
  return (
    <section id="levels" className="py-24 lg:py-32" style={{ background: T.bg2 }}>
      <Container>
        <SectionTitle
          eyebrow="Modular services"
          title="One destination. Different levels of support."
          subtitle="Modular services according to your needs."
        />
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {SUPPORT_LEVELS.map((l, i) => (
            <div key={l.k} className="rounded-2xl p-5 flex flex-col"
                 style={{ background: T.bg3, border: `1px solid ${T.border}`, borderTop: `2px solid ${i === 2 ? T.gold : T.leaf}66` }}>
              <div className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: T.mute }}>Level 0{i + 1}</div>
              <div className="mt-3 text-[14px] font-semibold leading-snug" style={{ color: T.ivory }}>{l.k}</div>
              <div className="mt-2 text-[12.5px] leading-relaxed" style={{ color: T.ivoryD }}>{l.s}</div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

function PilotSection() {
  return (
    <section id="pilot" className="py-24 lg:py-32" style={{ background: T.bg }}>
      <Container>
        <SectionTitle
          eyebrow="Pilot proposal"
          title="Start small. Validate the model."
          subtitle="A single, well-structured operation lets everyone — Ivory Tours, client, local guide, German-speaking support and Eco Collection — adjust the model before scaling."
        />
        {/* Recipe */}
        <div className="mt-12 rounded-2xl p-6 lg:p-8" style={{ background: T.bg2, border: `1px solid ${T.border}` }}>
          <Eyebrow color={T.gold}>Recipe for the pilot</Eyebrow>
          <div className="mt-6 grid md:grid-cols-5 gap-4">
            {PILOT_RECIPE.map((p, i) => (
              <div key={p.k} className="rounded-xl p-5"
                   style={{ background: T.bg3, border: `1px solid ${T.border}` }}>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center"
                       style={{ background: `${T.gold}18`, border: `1px solid ${T.gold}44` }}>
                    <p.icon size={16} style={{ color: T.gold }} />
                  </div>
                  <div className="text-[9.5px] uppercase tracking-[0.28em] font-mono" style={{ color: T.mute }}>0{i + 1}</div>
                </div>
                <div className="mt-4 text-[13.5px] font-semibold" style={{ color: T.ivory }}>{p.k}</div>
                {p.s && <div className="mt-0.5 text-[11.5px]" style={{ color: T.ivoryD }}>{p.s}</div>}
              </div>
            ))}
          </div>
          <div className="mt-6 text-center text-[12.5px]" style={{ color: T.ivoryD }}>
            Eco Collection coordinates the operation.
          </div>
        </div>

        {/* Feedback loop */}
        <div className="mt-10 rounded-2xl p-8 lg:p-10" style={{ background: `linear-gradient(135deg, ${T.bg2} 0%, rgba(201,162,74,0.06) 100%)`, border: `1px solid ${T.gold}44` }}>
          <Eyebrow color={T.gold}>Multi-stakeholder feedback loop</Eyebrow>
          <div className="mt-8 grid md:grid-cols-5 gap-3">
            {["Ivory Tours", "Client", "Local Guide / Operator", "German-speaking support", "Eco Collection"].map((n) => (
              <div key={n} className="rounded-xl p-4 text-center"
                   style={{ background: T.bg3, border: `1px solid ${T.border}` }}>
                <div className="text-[12.5px] font-semibold" style={{ color: T.ivory }}>{n}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[13.5px] italic text-center" style={{ color: T.gold }}>
            Adjust the model before scaling.
          </p>
        </div>
      </Container>
    </section>
  );
}

function QuestionsSection() {
  const [open, setOpen] = React.useState(0);
  return (
    <section id="questions" className="py-24 lg:py-32" style={{ background: T.bg2 }}>
      <Container>
        <SectionTitle
          eyebrow="What does Ivory Tours actually need?"
          title="Key questions to define the pilot parameters."
          subtitle="A short, honest conversation around these questions will shape the first operation."
        />
        <div className="mt-10 rounded-2xl overflow-hidden" style={{ background: T.bg3, border: `1px solid ${T.border}` }}>
          {QUESTIONS.map((q, i) => {
            const isOpen = open === i;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                className="w-full text-left px-6 py-5 flex items-start gap-5"
                style={{ borderTop: i === 0 ? "none" : `1px solid ${T.border}`, background: isOpen ? T.bg2 : "transparent" }}
              >
                <div className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center font-mono text-[12px] font-semibold"
                     style={{ background: `${T.gold}18`, border: `1px solid ${T.gold}44`, color: T.gold }}>
                  0{i + 1}
                </div>
                <div className="flex-1">
                  <div className="text-[14.5px] leading-snug" style={{ color: T.ivory }}>{q.split(" (")[0]}</div>
                  <AnimatePresence initial={false}>
                    {isOpen && q.includes("(") && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-2 text-[12.5px] italic" style={{ color: T.ivoryD }}>
                          {"(" + q.split("(")[1]}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <ChevronRight size={16}
                              style={{ color: T.gold, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 150ms" }} />
              </button>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

function NextStepsSection() {
  return (
    <section id="nextsteps" className="py-24 lg:py-32" style={{ background: T.bg }}>
      <Container>
        <SectionTitle
          eyebrow="Next steps"
          title="From pilot to long-term collaboration."
          subtitle="A clear sequence from the first operation to a reliable, scalable model."
        />
        <div className="mt-12 grid lg:grid-cols-[repeat(9,1fr)] gap-3 items-stretch">
          {NEXT_STEPS.map((n, i, arr) => (
            <React.Fragment key={n.k}>
              <div className="col-span-2 rounded-2xl p-5"
                   style={{ background: T.bg2, border: `1px solid ${T.border}`, borderTop: `2px solid ${T.leaf}66` }}>
                <div className="w-10 h-10 rounded-md flex items-center justify-center"
                     style={{ background: `${T.leaf}18`, border: `1px solid ${T.leaf}44` }}>
                  <n.icon size={18} style={{ color: T.leaf }} />
                </div>
                <div className="mt-4 text-[14px] font-semibold" style={{ color: T.ivory }}>{n.k}</div>
                <div className="mt-1 text-[12px] leading-relaxed" style={{ color: T.ivoryD }}>{n.s}</div>
              </div>
              {i < arr.length - 1 && (
                <div className="hidden lg:flex items-center justify-center" aria-hidden="true">
                  <ArrowRight size={18} style={{ color: T.gold, opacity: 0.7 }} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full text-[13px] uppercase tracking-[0.32em] font-semibold"
               style={{ background: `${T.gold}18`, border: `1px solid ${T.gold}66`, color: T.gold }}>
            Pilot first. Scale with evidence.
          </div>
        </div>
      </Container>
    </section>
  );
}

function CtaSection() {
  return (
    <section id="cta" className="relative py-24 lg:py-32 overflow-hidden" style={{ background: T.deep }}>
      <div className="absolute inset-0 opacity-30" aria-hidden="true">
        <img src="https://images.unsplash.com/photo-1570177638340-a3e4e9f3e6e5?auto=format&fit=crop&w=2400&q=70"
             alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${T.deep}cc 0%, ${T.deep} 100%)` }} />
      </div>
      <Container className="relative z-10 text-center">
        <Eyebrow color={T.gold}>Eco Collection × Ivory Tours</Eyebrow>
        <h2 className="mt-6 font-semibold tracking-tight text-[32px] sm:text-[40px] lg:text-[52px] leading-[1.08] max-w-3xl mx-auto"
            style={{ color: T.ivory, letterSpacing: "-0.025em" }}>
          Let’s define the <span style={{ color: T.gold }}>first pilot operation</span> in São Tomé &amp; Príncipe.
        </h2>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <a
            href="mailto:admin@bsgotech.com?subject=Ivory%20Tours%20%C2%B7%20Discuss%20the%20Pilot"
            data-testid="ivory-cta-discuss"
            className="inline-flex items-center gap-2 px-6 py-3 text-[13px] font-semibold rounded-md"
            style={{ background: T.gold, color: T.deep }}
          >
            Discuss the Pilot <ArrowRight size={14} />
          </a>
          <a
            href="/cabo-verde#eco"
            data-testid="ivory-cta-eco"
            className="inline-flex items-center gap-2 px-6 py-3 text-[13px] font-semibold rounded-md"
            style={{ background: "transparent", color: T.ivory, border: `1px solid ${T.borderS}` }}
          >
            View Eco Collection <ExternalLink size={14} />
          </a>
          <a
            href="/downloads/German_Tourism_Support_Sao_Tome.pdf"
            target="_blank" rel="noopener noreferrer" download
            data-testid="ivory-cta-download"
            className="inline-flex items-center gap-2 px-6 py-3 text-[13px] font-semibold rounded-md"
            style={{ background: `${T.ivory}10`, color: T.ivory, border: `1px solid ${T.borderS}` }}
          >
            Download Presentation <Download size={14} />
          </a>
        </div>
        <p className="mt-10 max-w-2xl mx-auto text-[11.5px] italic leading-relaxed" style={{ color: T.mute }}>
          Private material prepared for the Ivory Tours meeting on 16 October 2026. The German-speaking support model, CLA / USTP pilot structure and operational levels described above are proposed concepts — operational scale, commercial terms and partners remain to be confirmed with Ivory Tours.
        </p>
      </Container>
    </section>
  );
}

/* =========================== PRESENTATION MODE ======================== */
function PresentationMode({ onExit }) {
  const [idx, setIdx] = React.useState(0);
  const total = SECTIONS.length;

  const prev = React.useCallback(() => setIdx((v) => Math.max(0, v - 1)), []);
  const next = React.useCallback(() => setIdx((v) => Math.min(total - 1, v + 1)), [total]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") onExit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, onExit]);

  // Scroll the active section into view within the fullscreen container
  const containerRef = React.useRef(null);
  React.useEffect(() => {
    const el = document.getElementById(SECTIONS[idx].id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [idx]);

  return (
    <div className="fixed inset-0 z-[90] overflow-y-auto" style={{ background: T.deep }} ref={containerRef} data-testid="ivory-presentation-mode">
      {/* Controls */}
      <div className="fixed top-4 right-4 z-[100] flex items-center gap-2">
        <div className="px-3 py-1.5 rounded-md text-[10.5px] uppercase tracking-[0.28em] font-semibold"
             style={{ background: `${T.ivory}0f`, color: T.ivory, border: `1px solid ${T.borderS}` }}
             data-testid="ivory-presentation-progress">
          {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")} · {SECTIONS[idx].label}
        </div>
        <button type="button" onClick={onExit}
                aria-label="Exit presentation mode"
                data-testid="ivory-presentation-exit"
                className="w-9 h-9 rounded-md flex items-center justify-center"
                style={{ background: `${T.ivory}0f`, color: T.ivory, border: `1px solid ${T.borderS}` }}>
          <X size={16} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[99] h-[2px]" style={{ background: `${T.ivory}10` }}>
        <div className="h-full transition-all duration-300" style={{ width: `${((idx + 1) / total) * 100}%`, background: T.gold }} />
      </div>

      {/* Prev / Next */}
      <button type="button" onClick={prev}
              aria-label="Previous section" disabled={idx === 0}
              data-testid="ivory-presentation-prev"
              className="fixed left-4 top-1/2 -translate-y-1/2 z-[100] w-11 h-11 rounded-full flex items-center justify-center disabled:opacity-30"
              style={{ background: `${T.ivory}0f`, color: T.ivory, border: `1px solid ${T.borderS}` }}>
        <ChevronLeft size={18} />
      </button>
      <button type="button" onClick={next}
              aria-label="Next section" disabled={idx === total - 1}
              data-testid="ivory-presentation-next"
              className="fixed right-4 top-1/2 -translate-y-1/2 z-[100] w-11 h-11 rounded-full flex items-center justify-center disabled:opacity-30"
              style={{ background: `${T.ivory}0f`, color: T.ivory, border: `1px solid ${T.borderS}` }}>
        <ChevronRight size={18} />
      </button>

      {/* Slides stacked but we scroll to the active one. Each slide is at least min-h-screen so the deck feels full-screen. */}
      <div className="relative" style={{ fontSize: "0.96em" }}>
        <Hero />
        <NeedSection />
        <CapacitySection />
        <SupportSection />
        <OperatingSection />
        <UseCaseSection />
        <LevelsSection />
        <PilotSection />
        <QuestionsSection />
        <NextStepsSection />
        <CtaSection />
      </div>
    </div>
  );
}

/* =========================== PAGE =================================== */
export default function IvoryToursPresentation() {
  const [present, setPresent] = React.useState(false);

  // SEO: noindex + title + ?present=1 bootstrap
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = "Eco Collection × Ivory Tours — Private Presentation";

    // noindex, nofollow — override any existing robots meta (added by global SEOHead)
    const existing = document.querySelector('meta[name="robots"]');
    const prevContent = existing ? existing.getAttribute("content") : null;
    const robots = existing || document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, nofollow, noarchive, nosnippet");
    if (!existing) document.head.appendChild(robots);

    // open presentation mode if URL asks
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("present") === "1") setPresent(true);
    } catch { /* ignore */ }

    const onKey = (e) => { if (e.key === "p" || e.key === "P") setPresent((v) => !v); };
    window.addEventListener("keydown", onKey);

    return () => {
      document.title = prevTitle;
      if (existing && prevContent !== null) robots.setAttribute("content", prevContent);
      else if (robots.parentNode) robots.parentNode.removeChild(robots);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: T.bg, color: T.ivory, fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }} data-testid="ivory-tours-presentation">
      {/* Floating Present button */}
      <button
        type="button"
        onClick={() => setPresent(true)}
        data-testid="ivory-present-btn"
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 px-4 py-2.5 text-[12px] font-semibold rounded-full shadow-lg"
        style={{ background: T.gold, color: T.deep, border: `1px solid ${T.goldD}` }}
      >
        <Play size={13} /> Presentation Mode <span style={{ opacity: 0.6, marginLeft: 4 }}>P</span>
      </button>

      {/* Normal scrolling page */}
      <Hero />
      <NeedSection />
      <CapacitySection />
      <SupportSection />
      <OperatingSection />
      <UseCaseSection />
      <LevelsSection />
      <PilotSection />
      <QuestionsSection />
      <NextStepsSection />
      <CtaSection />

      {/* Footer */}
      <footer className="py-10 border-t" style={{ borderColor: T.border, background: T.deep }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px]" style={{ color: T.mute }}>
          <div>Private material · Eco Collection × Ivory Tours · by BSGOTECH · 2026</div>
          <div>Not public. Not indexed. Prepared for the 16 October 2026 meeting.</div>
        </div>
      </footer>

      {/* Presentation mode overlay */}
      {present && <PresentationMode onExit={() => setPresent(false)} />}
    </div>
  );
}
